'use client';

import { useState } from 'react';

import { cn } from '@/utils/cn';

import {
  BENCHMARKS,
  type BenchmarkId,
  CONFIGS,
  type Config,
  DATA_AS_OF,
  PROVIDER_COLORS,
  SOURCE
} from './data';
import {
  Info,
  SlidersHorizontal,
  TrendingUp,
  Trophy,
  Users
} from 'lucide-react';

const HARNESSES = ['Pi', 'Claude Code', 'Codex', 'Grok Build'];
const PROVIDERS = Object.keys(PROVIDER_COLORS);
const TABLE_COLUMNS: BenchmarkId[] = [
  'overall',
  'txbench',
  'epibench',
  'variantbench'
];

type View = 'best' | 'all' | string;

const scoreOf = (config: Config, id: BenchmarkId) => config.scores[id];
const fmt = (value: number | null) =>
  value === null ? '—' : `${value.toFixed(1)}%`;
const label = (config: Config) => `${config.model} · ${config.harness}`;

/** Highest first; configs without a score for this benchmark are dropped. */
const rank = (configs: Config[], id: BenchmarkId) =>
  configs
    .filter((c) => scoreOf(c, id) !== null)
    .sort((a, b) => (scoreOf(b, id) ?? 0) - (scoreOf(a, id) ?? 0));

const bestPerModel = (configs: Config[], id: BenchmarkId) => {
  const seen = new Set<string>();
  return rank(configs, id).filter(
    (c) => !seen.has(c.model) && seen.add(c.model)
  );
};

const median = (values: number[]) => {
  const sorted = [...values].sort((a, b) => a - b);
  const mid = Math.floor(sorted.length / 2);
  return sorted.length % 2 ? sorted[mid] : (sorted[mid - 1] + sorted[mid]) / 2;
};

/** How much the Pi harness beats Claude Code when the same model runs under both. */
const harnessEffect = (id: BenchmarkId) => {
  const diffs = [...new Set(CONFIGS.map((c) => c.model))].flatMap((model) => {
    const pi = CONFIGS.find((c) => c.model === model && c.harness === 'Pi');
    const cc = CONFIGS.find(
      (c) => c.model === model && c.harness === 'Claude Code'
    );
    const a = pi && scoreOf(pi, id);
    const b = cc && scoreOf(cc, id);
    return a == null || b == null ? [] : [a - b];
  });
  return {
    pairs: diffs.length,
    piWins: diffs.filter((d) => d > 0).length,
    average: diffs.reduce((sum, d) => sum + d, 0) / (diffs.length || 1)
  };
};

const chipClass = (active: boolean) =>
  cn(
    'inline-flex items-center gap-2 whitespace-nowrap rounded-full border px-3.5 py-2 text-sm font-semibold transition-colors cursor-pointer',
    active
      ? 'border-[#0E3D2D] bg-[#0E3D2D] text-white dark:border-[#9ae8c9] dark:bg-[#9ae8c9] dark:text-[#0E3D2D]'
      : 'border-border bg-muted/10 text-muted-foreground hover:bg-muted/30 hover:text-foreground'
  );

const ProviderLogo = ({ provider }: { provider: string }) => {
  switch (provider) {
    case 'Anthropic':
      return (
        <svg
          aria-hidden='true'
          viewBox='0 0 24 24'
          fill='currentColor'
          className='text-foreground size-5 shrink-0 drop-shadow-sm'>
          <path d='M17.3041 3.541h-3.6718l6.696 16.918H24Zm-10.6082 0L0 20.459h3.7442l1.3693-3.5527h7.0052l1.3693 3.5528h3.7442L10.5363 3.5409Zm-.3712 10.2232 2.2914-5.9456 2.2914 5.9456Z' />
        </svg>
      );
    case 'OpenAI':
      return (
        <svg
          aria-hidden='true'
          viewBox='0 0 20 20'
          fill='currentColor'
          className='text-foreground size-5 shrink-0 drop-shadow-sm'>
          <path d='M11.248 18.25q-.825 0-1.568-.314a4.3 4.3 0 0 1-1.32-.874 4 4 0 0 1-1.304.214 4 4 0 0 1-2.046-.544 4.27 4.27 0 0 1-1.518-1.485 4 4 0 0 1-.56-2.095q0-.48.131-1.04A4.4 4.4 0 0 1 2.04 10.71a4.07 4.07 0 0 1 .017-3.4 4.2 4.2 0 0 1 1.056-1.418 3.8 3.8 0 0 1 1.6-.842 3.9 3.9 0 0 1 .76-1.683q.593-.759 1.451-1.188a4.04 4.04 0 0 1 1.832-.429q.825 0 1.567.313.742.314 1.32.875a4 4 0 0 1 1.304-.215q1.106 0 2.046.545a4.14 4.14 0 0 1 1.501 1.485q.578.941.578 2.095 0 .48-.132 1.04.66.61 1.023 1.419.363.792.363 1.666 0 .892-.38 1.717a4.3 4.3 0 0 1-1.072 1.435 3.8 3.8 0 0 1-1.584.825 3.8 3.8 0 0 1-.775 1.683 4.06 4.06 0 0 1-1.436 1.188 4.04 4.04 0 0 1-1.832.429m-4.076-2.062q.825 0 1.435-.347l3.103-1.782a.36.36 0 0 0 .164-.313v-1.42L7.881 14.62a.67.67 0 0 1-.726 0l-3.118-1.798a.5.5 0 0 1-.017.115v.198q0 .841.396 1.551.413.693 1.139 1.089a3.2 3.2 0 0 0 1.617.412m.165-2.69a.4.4 0 0 0 .181.05q.083 0 .165-.05l1.238-.71-3.977-2.31a.7.7 0 0 1-.363-.643v-3.58q-.825.362-1.32 1.122a2.9 2.9 0 0 0-.495 1.65q0 .809.413 1.55.412.743 1.072 1.123zm3.91 3.663q.875 0 1.585-.396a2.96 2.96 0 0 0 1.534-2.64v-3.564a.32.32 0 0 0-.165-.297l-1.254-.726v4.604a.7.7 0 0 1-.363.643l-3.119 1.799a3 3 0 0 0 1.783.577m.627-6.039V8.878L10.01 7.822 8.129 8.878v2.244l1.881 1.056zM7.057 5.859a.7.7 0 0 1 .363-.644l3.119-1.798a3 3 0 0 0-1.782-.578q-.874 0-1.584.396A2.96 2.96 0 0 0 6.05 4.324a3.07 3.07 0 0 0-.396 1.551v3.547q0 .199.165.314l1.237.726zm8.383 7.887q.825-.364 1.303-1.123.495-.758.495-1.65a3.15 3.15 0 0 0-.412-1.55q-.413-.743-1.073-1.123l-3.086-1.782q-.099-.065-.181-.049a.3.3 0 0 0-.165.05l-1.238.692 3.993 2.327a.6.6 0 0 1 .264.264.64.64 0 0 1 .1.363zm-3.317-8.382a.63.63 0 0 1 .726 0l3.135 1.831v-.297q0-.792-.396-1.501a2.86 2.86 0 0 0-1.105-1.155q-.71-.43-1.65-.43-.825 0-1.436.347L8.294 5.941a.36.36 0 0 0-.165.314v1.418z' />
        </svg>
      );
    case 'Google':
      return (
        <svg
          aria-hidden='true'
          viewBox='0 0 24 24'
          fill='#4285F4'
          className='size-5 shrink-0 drop-shadow-sm'>
          <path d='M12 2L14.5 9.5L22 12L14.5 14.5L12 22L9.5 14.5L2 12L9.5 9.5L12 2Z' />
        </svg>
      );
    case 'SpaceXAI':
      return (
        <svg
          aria-hidden='true'
          viewBox='0 0 24 24'
          fill='currentColor'
          className='text-foreground size-5 shrink-0 drop-shadow-sm'>
          <path d='M14.234 10.162 22.977 0h-2.072l-7.591 8.824L7.251 0H.258l9.168 13.343L.258 24H2.33l8.016-9.318L16.749 24h6.993zm-2.837 3.299-.929-1.329L3.076 1.56h3.182l5.965 8.532.929 1.329 7.754 11.09h-3.182z' />
        </svg>
      );
    default:
      return (
        <div
          aria-hidden='true'
          className='bg-muted text-muted-foreground flex size-5 shrink-0 items-center justify-center rounded-xl font-sans text-[10px] leading-none font-bold shadow-sm'>
          {provider[0]}
        </div>
      );
  }
};

function Stat({
  title,
  value,
  detail
}: {
  title: string;
  value: string;
  detail: string;
}) {
  return (
    <div className='border-border/40 bg-card rounded-2xl border p-5 shadow-sm'>
      <p className='text-muted-foreground text-xs font-bold tracking-wider uppercase'>
        {title}
      </p>
      <p className='text-foreground mt-2 font-serif text-2xl leading-tight font-bold sm:text-3xl'>
        {value}
      </p>
      <p className='text-muted-foreground mt-1 text-sm'>{detail}</p>
    </div>
  );
}

export default function LLMBenchmarks() {
  const [benchmark, setBenchmark] = useState<BenchmarkId>('overall');
  const [provider, setProvider] = useState<string | null>(null);
  const [view, setView] = useState<View>('best');

  const info = BENCHMARKS.find((b) => b.id === benchmark) ?? BENCHMARKS[0];

  // Takeaways always describe the whole leaderboard, not the filtered view
  const allBest = bestPerModel(CONFIGS, benchmark);
  const leader = allBest[0];
  const leaderScore = scoreOf(leader, benchmark) ?? 0;
  const medianScore = median(allBest.map((c) => scoreOf(c, benchmark) ?? 0));
  const effect = harnessEffect(benchmark);
  const axisMax =
    Math.ceil(
      Math.max(...CONFIGS.map((c) => scoreOf(c, benchmark) ?? 0)) / 10
    ) * 10;

  const byProvider = CONFIGS.filter(
    (c) => !provider || c.provider === provider
  );
  const byHarness = byProvider.filter(
    (c) => view === 'best' || view === 'all' || c.harness === view
  );
  const bars =
    view === 'best'
      ? bestPerModel(byHarness, benchmark)
      : rank(byHarness, benchmark);
  const tableRows = [...byHarness].sort(
    (a, b) => (scoreOf(b, benchmark) ?? -1) - (scoreOf(a, benchmark) ?? -1)
  );
  const columnMax = Object.fromEntries(
    TABLE_COLUMNS.map((id) => [
      id,
      Math.max(...CONFIGS.map((c) => scoreOf(c, id) ?? 0))
    ])
  ) as Record<BenchmarkId, number>;

  const hasFilters = provider !== null || view !== 'best';

  const opus55 = CONFIGS.find(
    (c) => c.model === 'Claude Opus 5.5' && c.harness === 'Pi'
  );
  const opus5 = CONFIGS.find(
    (c) => c.model === 'Claude Opus 5' && c.harness === 'Pi'
  );

  return (
    <div className='relative mx-auto max-w-6xl space-y-10 px-4 py-8 sm:space-y-14 sm:px-6 lg:px-8'>
      {/* Header */}
      <section className='relative overflow-hidden rounded-2xl bg-[#0E3D2D] p-8 text-center shadow-2xl sm:p-12 lg:p-16'>
        <div
          className='pointer-events-none absolute inset-0 opacity-20'
          style={{
            backgroundImage: `url("data:image/svg+xml,%3Csvg width='32' height='32' viewBox='0 0 32 32' fill='none' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M16 12V20M12 16H20' stroke='%23ffffff' stroke-width='1.5' stroke-linecap='round'/%3E%3C/svg%3E")`,
            backgroundSize: '32px 32px'
          }}
        />
        <div className='relative z-10 mx-auto flex max-w-3xl flex-col items-center space-y-6'>
          <h1 className='font-serif text-4xl leading-[1.1] font-medium tracking-tight text-white sm:text-5xl lg:text-6xl'>
            Which LLM is best at{' '}
            <span className='text-[#9ae8c9]'>bioinformatics?</span>
          </h1>
          <p className='max-w-2xl text-base leading-relaxed text-[#9ae8c9]/85 sm:text-lg'>
            Pass rates for {new Set(CONFIGS.map((c) => c.model)).size} frontier
            models on agentic biology benchmarks (TxBench-PP, EpiBench,
            VariantBench), including Claude Opus 5.5, GPT-6 Astra, Grok 4.7 and
            Gemini 3.8 Flash.
          </p>
        </div>
      </section>

      {/* Benchmark picker + takeaways */}
      <section className='space-y-6'>
        <div
          role='group'
          aria-label='Benchmark'
          className='bg-muted/20 grid grid-cols-2 gap-2 rounded-2xl p-1.5 sm:grid-cols-4'>
          {BENCHMARKS.map((b) => (
            <button
              key={b.id}
              type='button'
              aria-pressed={benchmark === b.id}
              onClick={() => setBenchmark(b.id)}
              className={cn(
                'cursor-pointer rounded-xl px-3 py-3 text-sm font-bold transition-colors',
                benchmark === b.id
                  ? 'bg-card text-foreground ring-border shadow-sm ring-1'
                  : 'text-muted-foreground hover:bg-muted/30 hover:text-foreground'
              )}>
              {b.name}
            </button>
          ))}
        </div>

        <p className='text-foreground/80 text-base'>{info.blurb}</p>

        <div className='grid gap-4 md:grid-cols-3'>
          <Stat
            title='Leader'
            value={`${leader.model} ${fmt(leaderScore)}`}
            detail={`Best run: ${leader.harness} harness. #2 model: ${allBest[1].model} at ${fmt(scoreOf(allBest[1], benchmark))}.`}
          />
          <Stat
            title='Typical model'
            value={fmt(medianScore)}
            detail={`Median of ${allBest.length} models, each at its best harness. The leader is ${(leaderScore - medianScore).toFixed(1)} points above it.`}
          />
          <Stat
            title='Harness matters'
            value={`Pi wins ${effect.piWins} of ${effect.pairs}`}
            detail={`Same model, Pi vs Claude Code: Pi scores ${effect.average >= 0 ? '+' : ''}${effect.average.toFixed(1)} points on average.`}
          />
        </div>
      </section>

      {/* Leaderboard */}
      <section className='border-border/40 bg-card space-y-5 rounded-2xl border p-5 shadow-sm sm:p-8'>
        <div className='flex flex-wrap items-start justify-between gap-3'>
          <div>
            <h2 className='text-foreground flex items-center gap-2 font-serif text-2xl font-bold tracking-tight'>
              <Trophy
                className='size-6 text-[#066653] dark:text-[#a3f7bf]'
                aria-hidden='true'
              />
              {info.name} leaderboard
            </h2>
            <p className='text-muted-foreground mt-1 text-sm'>
              Share of tasks passed (%). Longer bar is better.
            </p>
          </div>
          {hasFilters && (
            <button
              type='button'
              onClick={() => {
                setProvider(null);
                setView('best');
              }}
              className='text-sm font-bold text-[#066653] hover:underline dark:text-[#a3f7bf]'>
              Reset filters
            </button>
          )}
        </div>

        {/* Filters: provider chips double as the colour legend */}
        <div className='space-y-3'>
          <div
            className='-mx-1 flex gap-2 overflow-x-auto px-1 pb-1'
            role='group'
            aria-label='Developer'>
            <button
              type='button'
              aria-pressed={provider === null}
              onClick={() => setProvider(null)}
              className={chipClass(provider === null)}>
              <Users
                className='size-4'
                aria-hidden='true'
              />
              All developers
            </button>
            {PROVIDERS.map((p) => (
              <button
                key={p}
                type='button'
                aria-pressed={provider === p}
                onClick={() => setProvider(provider === p ? null : p)}
                className={chipClass(provider === p)}>
                <span
                  className='size-2.5 rounded-full'
                  style={{ backgroundColor: PROVIDER_COLORS[p] }}
                  aria-hidden='true'
                />
                {p}
              </button>
            ))}
          </div>
          <div
            className='-mx-1 flex items-center gap-2 overflow-x-auto px-1 pb-1'
            role='group'
            aria-label='Show'>
            <SlidersHorizontal
              className='text-muted-foreground size-4 shrink-0'
              aria-hidden='true'
            />
            {[
              { id: 'best', name: 'Best run per model' },
              { id: 'all', name: 'Every run' },
              ...HARNESSES.map((h) => ({ id: h, name: `${h} only` }))
            ].map((option) => (
              <button
                key={option.id}
                type='button'
                aria-pressed={view === option.id}
                onClick={() => setView(option.id)}
                className={chipClass(view === option.id)}>
                {option.name}
              </button>
            ))}
          </div>
        </div>

        {bars.length === 0 ? (
          <p className='text-muted-foreground py-12 text-center'>
            No results for this combination. Try resetting the filters.
          </p>
        ) : (
          <ol className='space-y-3'>
            {bars.map((config, index) => {
              const score = scoreOf(config, benchmark) ?? 0;
              return (
                <li
                  key={label(config)}
                  className='flex items-start gap-3'>
                  <span className='text-muted-foreground w-7 shrink-0 pt-0.5 text-right font-mono text-sm font-bold'>
                    {index + 1}
                  </span>
                  <div className='min-w-0 flex-1 sm:flex sm:items-center sm:gap-4'>
                    <div className='flex items-center gap-2 sm:w-64 sm:shrink-0'>
                      <ProviderLogo provider={config.provider} />
                      <span className='text-foreground truncate text-sm font-bold'>
                        {config.model}
                      </span>
                      {view !== 'best' && (
                        <span className='bg-muted/40 text-muted-foreground shrink-0 rounded-md px-1.5 py-0.5 text-xs font-semibold'>
                          {config.harness}
                        </span>
                      )}
                    </div>
                    <div className='mt-1.5 flex flex-1 items-center gap-3 sm:mt-0'>
                      <div
                        className='bg-muted/30 h-3.5 flex-1 overflow-hidden rounded-full'
                        aria-hidden='true'>
                        <div
                          className='h-full rounded-full'
                          style={{
                            width: `${(score / axisMax) * 100}%`,
                            backgroundColor: PROVIDER_COLORS[config.provider]
                          }}
                        />
                      </div>
                      <span className='text-foreground w-14 shrink-0 text-right font-mono text-sm font-black'>
                        {fmt(score)}
                      </span>
                    </div>
                  </div>
                </li>
              );
            })}
          </ol>
        )}
        <p className='text-muted-foreground text-xs'>
          {view === 'best'
            ? 'Each model shows the harness (agent framework) where it scored highest. Switch to “Every run” to see all harnesses.'
            : 'Each row is one model running in one agent harness.'}{' '}
          Bars span 0–{axisMax}%.
        </p>
      </section>

      {/* Cross-benchmark table */}
      <section className='space-y-4'>
        <div>
          <h2 className='text-foreground font-serif text-2xl font-bold tracking-tight'>
            Compare across benchmarks
          </h2>
          <p className='text-muted-foreground mt-1 text-sm'>
            Every run, sorted by {info.name}. Darker green means a higher score
            within that column. Scroll sideways on small screens.
          </p>
        </div>
        <div className='border-border/45 bg-card overflow-x-auto rounded-2xl border'>
          <table className='w-full min-w-[560px] border-collapse text-left text-sm'>
            <thead>
              <tr className='border-border/40 bg-muted/30 text-muted-foreground border-b text-xs font-bold tracking-wider uppercase'>
                <th
                  scope='col'
                  className='bg-muted sticky left-0 z-10 px-4 py-3'>
                  Model · harness
                </th>
                {TABLE_COLUMNS.map((id) => (
                  <th
                    key={id}
                    scope='col'
                    className={cn(
                      'px-3 py-3 text-right',
                      id === benchmark && 'text-foreground'
                    )}>
                    {BENCHMARKS.find((b) => b.id === id)?.name}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className='divide-border/20 divide-y'>
              {tableRows.map((config) => (
                <tr key={label(config)}>
                  <th
                    scope='row'
                    className='bg-card sticky left-0 z-10 px-4 py-3 text-left font-semibold'>
                    <div className='flex items-center gap-2'>
                      <ProviderLogo provider={config.provider} />
                      <div>
                        <div className='text-foreground whitespace-nowrap'>
                          {config.model}
                        </div>
                        <div className='text-muted-foreground text-xs font-medium'>
                          {config.harness}
                        </div>
                      </div>
                    </div>
                  </th>
                  {TABLE_COLUMNS.map((id) => {
                    const score = scoreOf(config, id);
                    return (
                      <td
                        key={id}
                        className={cn(
                          'px-3 py-3 text-right font-mono tabular-nums',
                          id === benchmark
                            ? 'text-foreground font-black'
                            : 'text-foreground/80'
                        )}
                        style={
                          score === null
                            ? undefined
                            : {
                                backgroundColor: `color-mix(in srgb, #10a37f ${(score / columnMax[id]) * 35}%, transparent)`
                              }
                        }>
                        {fmt(score)}
                        {id === 'overall' && config.coverage !== '12/12' && (
                          <span
                            title={`Scored on ${config.coverage} benchmarks`}
                            className='text-muted-foreground'>
                            *
                          </span>
                        )}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className='text-muted-foreground text-xs'>
          * Overall is averaged over the benchmarks this run completed (11 of
          12). A dash means the run has no score for that benchmark.
        </p>
      </section>

      {/* Notes */}
      <section className='border-border/40 bg-card space-y-5 rounded-2xl border p-5 shadow-sm sm:p-8'>
        <h2 className='text-foreground flex items-center gap-2 font-serif text-2xl font-bold tracking-tight'>
          <TrendingUp
            className='size-5 text-[#066653] dark:text-[#a3f7bf]'
            aria-hidden='true'
          />
          How to read this
        </h2>
        <ul className='text-muted-foreground grid gap-5 text-sm leading-relaxed md:grid-cols-2'>
          <li>
            <strong className='text-foreground'>What is scored.</strong> An AI
            agent gets a real bioinformatics task and must submit a structured
            answer. Verifiable graders check it against a reference. The score
            is the share of tasks passed.
          </li>
          <li>
            <strong className='text-foreground'>What a harness is.</strong> The
            agent framework wrapped around the model (Pi, Claude Code, Codex,
            Grok Build). The same model can score differently in each.
          </li>
          <li>
            <strong className='text-foreground'>
              Treat small gaps as ties.
            </strong>{' '}
            Each benchmark has about 100 tasks. The benchmark papers report 95%
            confidence intervals of roughly ±8 points (for example TxBench-PP:
            59.3%, interval 51.1–67.6), so gaps under about 5 points are not
            meaningful.
          </li>
          {opus55 && opus5 && (
            <li>
              <strong className='text-foreground'>
                Look across benchmarks.
              </strong>{' '}
              Claude Opus 5.5 scores {fmt(opus55.scores.epibench)} on EpiBench
              and {fmt(opus55.scores.txbench)} on TxBench-PP, while Claude Opus
              5 scores {fmt(opus5.scores.epibench)} and{' '}
              {fmt(opus5.scores.txbench)}. One overall number hides big
              differences between tasks.
            </li>
          )}
          <li>
            <strong className='text-foreground'>Missing models.</strong> Claude
            Sonnet 5.5 and Fable 5.1 have no published results on these
            benchmarks yet, so they are not shown.
          </li>
        </ul>
        <p className='text-muted-foreground text-xs'>
          Data: LatchBio,{' '}
          <a
            href={SOURCE.href}
            target='_blank'
            rel='noopener noreferrer'
            className='underline'>
            {SOURCE.label}
          </a>
          , as of {DATA_AS_OF}. Benchmarks:{' '}
          <a
            href='https://arxiv.org/abs/2606.19245'
            target='_blank'
            rel='noopener noreferrer'
            className='underline'>
            TxBench-PP
          </a>
          ,{' '}
          <a
            href='https://arxiv.org/abs/2606.13602'
            target='_blank'
            rel='noopener noreferrer'
            className='underline'>
            EpiBench
          </a>
          ,{' '}
          <a
            href='https://latch.bio/variantbench'
            target='_blank'
            rel='noopener noreferrer'
            className='underline'>
            VariantBench
          </a>
          .
        </p>
      </section>
    </div>
  );
}
